import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Users,
  Calendar,
  DollarSign,
  Download,
  RefreshCw
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Cell,
  Pie
} from 'recharts';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import StatsCard from '@/components/dashboard/StatsCard';
import { ProtectedComponent, SensitiveDataProtection } from '@/components/common/ProtectedComponent';
import { Permission } from '@/types/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { formatNZD } from '@/utils/currency';
import { getNzDateString } from '@/utils/nzDate';
import { logger } from '@/utils/logger';
import { DashboardStats } from '@/types';
import i18n from '../i18n';

const AUCKLAND_TIME_ZONE = 'Pacific/Auckland';

const escapeCsvValue = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value);
  const needsQuotes = /[",\n\r]/.test(text);
  const escaped = text.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const downloadCsv = (filename: string, csvContent: string) => {
  const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatShortDate = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: AUCKLAND_TIME_ZONE,
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const addDaysToDateString = (dateString: string, days: number) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + days);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: AUCKLAND_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(base);
};

const enumerateDateStrings = (from: string, to: string) => {
  if (!from || !to) return [];
  const result: string[] = [];
  let current = from;
  for (let guard = 0; guard < 370; guard += 1) {
    result.push(current);
    if (current === to) break;
    current = addDaysToDateString(current, 1);
  }
  return result;
};

type TrendPoint = { date: string; date_full: string; revenue: number; bookings: number };
type UtilizationPoint = { court: string; utilization: number; bookings: number; revenue: number };
type BookingTypeSlice = { name: string; value: number; color: string };
type PeakHourPoint = { hour: string; bookings: number };

const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canViewRevenue = hasPermission(Permission.VIEW_REVENUE);

  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days'>('7days');
  const [selectedMetric, setSelectedMetric] = useState<'revenue' | 'bookings'>(
    canViewRevenue ? 'revenue' : 'bookings'
  );

  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [courtUtilizationData, setCourtUtilizationData] = useState<UtilizationPoint[]>([]);
  const [bookingTypeData, setBookingTypeData] = useState<BookingTypeSlice[]>([
    { name: i18n.t('analytics.onlineBookings'), value: 0, color: '#0ea5e9' },
    { name: i18n.t('analytics.walkInBookings'), value: 0, color: '#22c55e' },
  ]);
  const [peakHoursData, setPeakHoursData] = useState<PeakHourPoint[]>([]);
  const [cancelledBookings, setCancelledBookings] = useState(0);

  const rangeDays = useMemo(() => {
    if (dateRange === '30days') return 30;
    if (dateRange === '90days') return 90;
    return 7;
  }, [dateRange]);

  const totalRevenue = useMemo(
    () => trendData.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0),
    [trendData]
  );
  const totalBookings = useMemo(
    () => trendData.reduce((sum, row) => sum + (Number(row.bookings) || 0), 0),
    [trendData]
  );

  const cancellationRate = useMemo(() => {
    if (!totalBookings) return 0;
    return (cancelledBookings / totalBookings) * 100;
  }, [cancelledBookings, totalBookings]);

  const peakHourLabel = useMemo(() => {
    if (!peakHoursData.length) return '—';
    const best = peakHoursData.reduce((max, cur) => (cur.bookings > max.bookings ? cur : max), peakHoursData[0]);
    return best.hour || '—';
  }, [peakHoursData]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);

    try {
      const endDate = getNzDateString();
      const startDate = addDaysToDateString(endDate, -(rangeDays - 1));
      setDateFrom(startDate);
      setDateTo(endDate);

      const dateStrings = enumerateDateStrings(startDate, endDate);

      const dashboardPromise = api.analytics.getDashboardStats(dateRange);
      const bookingTrendPromise = api.analytics.getBookingTrend({ date_from: startDate, date_to: endDate });
      const utilizationPromise = api.analytics.getCourtUtilization({ date_from: startDate, date_to: endDate });
      const peakPromise = api.analytics.getPeakHours({ date_from: startDate, date_to: endDate });

      const revenuePromise = canViewRevenue
        ? api.analytics.getRevenueReport({ date_from: startDate, date_to: endDate, group_by: 'day' })
        : Promise.resolve({ data: [] as unknown[] });

      const [dashboardRes, bookingTrendRes, utilizationRes, peakRes, revenueRes] = await Promise.all([
        dashboardPromise,
        bookingTrendPromise,
        utilizationPromise,
        peakPromise,
        revenuePromise
      ]);

      setDashboardStats(dashboardRes.data || null);

      const bookingsByDate = new Map<string, number>();
      const revenueByDate = new Map<string, number>();
      const bookingTypeTotals = { online: 0, walk_in: 0 };
      let cancelledTotal = 0;

      if (Array.isArray(bookingTrendRes.data)) {
        for (const row of bookingTrendRes.data) {
          const record = row as Record<string, unknown>;
          const date = String(record.date ?? '');
          const total = Number(record.total ?? 0);
          const online = Number(record.online ?? 0);
          const walkIn = Number(record.walk_in ?? 0);
          const cancelled = Number(record.cancelled ?? 0);

          if (date) bookingsByDate.set(date, total);
          bookingTypeTotals.online += online;
          bookingTypeTotals.walk_in += walkIn;
          cancelledTotal += cancelled;
        }
      }

      if (Array.isArray(revenueRes.data)) {
        for (const row of revenueRes.data) {
          const record = row as Record<string, unknown>;
          const date = String(record.period ?? '');
          const revenue = Number(record.revenue ?? 0);
          if (date) revenueByDate.set(date, revenue);
        }
      }

      setCancelledBookings(cancelledTotal);

      setTrendData(
        dateStrings.map((dateString) => ({
          date: formatShortDate(dateString),
          date_full: dateString,
          revenue: revenueByDate.get(dateString) || 0,
          bookings: bookingsByDate.get(dateString) || 0
        }))
      );

      const mappedUtilization: UtilizationPoint[] = [];
      if (Array.isArray(utilizationRes.data)) {
        for (const row of utilizationRes.data) {
          const record = row as Record<string, unknown>;
          mappedUtilization.push({
            court: String(record.court_name ?? record.court ?? ''),
            utilization: Number(record.utilization_rate ?? record.utilization ?? 0),
            bookings: Number(record.booked_slots ?? record.bookings ?? 0),
            revenue: Number(record.revenue ?? 0)
          });
        }
      }
      setCourtUtilizationData(mappedUtilization);

      const mappedPeak: PeakHourPoint[] = [];
      if (Array.isArray(peakRes.data)) {
        for (const row of peakRes.data) {
          const record = row as Record<string, unknown>;
          mappedPeak.push({
            hour: String(record.hour_display ?? record.hour ?? ''),
            bookings: Number(record.booking_count ?? record.bookings ?? 0)
          });
        }
      }
      setPeakHoursData(mappedPeak);

      setBookingTypeData([
        { name: i18n.t('analytics.onlineBookings'), value: bookingTypeTotals.online, color: '#0ea5e9' },
        { name: i18n.t('analytics.walkInBookings'), value: bookingTypeTotals.walk_in, color: '#22c55e' },
      ]);
    } catch (error) {
      logger.error('Analytics load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, rangeDays, canViewRevenue]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleExport = () => {
    if (!trendData.length) {
      toast.error(t('common.noDataToExport'));
      return;
    }

    const headers = ['date', 'revenue_nzd', 'bookings'];
    const lines = [
      headers.join(','),
      ...trendData.map((row) =>
        [row.date_full, row.revenue, row.bookings].map(escapeCsvValue).join(',')
      ),
    ];

    const filename = `analytics_trend_${dateFrom || 'from'}_to_${dateTo || 'to'}.csv`;
    downloadCsv(filename, lines.join('\n'));
    toast.success(t('common.exportSuccess', { count: trendData.length }));
  };

  const utilizationValue = dashboardStats?.avg_utilization?.value;
  const utilizationRate = utilizationValue ? Number(utilizationValue) : 0;

  const activeUsersValue = dashboardStats?.active_users?.value;
  const activeUsers = activeUsersValue ? Number(activeUsersValue) : bookingTypeData.reduce((sum, row) => sum + row.value, 0);

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('analytics.title')}</h1>
          <p className="text-gray-500">{t('analytics.overview')}</p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="7days">{t('common.thisWeek')}</option>
            <option value="30days">{t('common.thisMonth')}</option>
            <option value="90days">{t('analytics.last90Days')}</option>
          </select>

          <button className="btn btn-secondary gap-2" onClick={loadAnalytics} disabled={loading}>
            <RefreshCw className="h-4 w-4" />
            {t('common.refresh')}
          </button>

          <button className="btn btn-primary gap-2" onClick={handleExport} disabled={loading}>
            <Download className="h-4 w-4" />
            {t('analytics.exportData')}
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <StatsCard
          title={`${t('analytics.revenue')} / Revenue`}
          value={formatNZD(totalRevenue, { maximumFractionDigits: 0 })}
          icon={DollarSign}
          color="success"
          trend="neutral"
          sensitive={true}
          sensitiveLevel="financial"
          loading={false}
        />

        <StatsCard
          title={`${t('analytics.totalBookings')} / Bookings`}
          value={totalBookings}
          icon={Calendar}
          color="primary"
          trend="neutral"
          loading={false}
        />

        <StatsCard
          title={`${t('dashboard.activeUsers')} / Customers`}
          value={activeUsers}
          icon={Users}
          color="warning"
          trend="neutral"
          loading={false}
        />

        <StatsCard
          title={`${t('analytics.utilizationRate')} / Utilization`}
          value={`${utilizationRate.toFixed(1)}%`}
          icon={BarChart3}
          color="primary"
          trend="neutral"
          loading={false}
        />
      </div>

      <div className="text-xs text-gray-500">
        {`Revenue ${formatNZD(totalRevenue, { maximumFractionDigits: 0 })} | Bookings ${totalBookings} | Customers ${activeUsers} | Utilization ${utilizationRate.toFixed(1)}%`}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="card card-comfortable"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('analytics.bookingTrends')}</h3>
              <p className="text-sm text-gray-500">
                {dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : t('analytics.operationalDataChanges')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ProtectedComponent permission={Permission.VIEW_REVENUE}>
                <button
                  onClick={() => setSelectedMetric('revenue')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedMetric === 'revenue'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('analytics.revenue')}
                </button>
              </ProtectedComponent>

              <button
                onClick={() => setSelectedMetric('bookings')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  selectedMetric === 'bookings'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('analytics.bookings')}
              </button>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="card card-comfortable"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('analytics.courtUtilization')}</h3>
              <p className="text-sm text-gray-500">{t('analytics.courtUsageAndRevenue')}</p>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={courtUtilizationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="court" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="utilization" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="card card-comfortable"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.bookingTypeDistribution')}</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={bookingTypeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => {
                    const total = bookingTypeData.reduce((sum, row) => sum + row.value, 0);
                    const percent = total ? Math.round((Number(value) / total) * 100) : 0;
                    return `${name} ${percent}%`;
                  }}
                >
                  {bookingTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="card card-comfortable"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.peakHours')}</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peakHoursData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Bar dataKey="bookings" fill="#22c55e" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="card card-comfortable"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.keyInsights')}</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('analytics.peakHour')}</span>
              <span className="text-sm font-semibold text-gray-900">{peakHourLabel}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('analytics.averageBookingValue')}</span>
              <SensitiveDataProtection
                level="financial"
                placeholder={<span className="text-sm font-semibold text-gray-400">***</span>}
              >
                <span className="text-sm font-semibold text-gray-900">
                  {totalBookings ? formatNZD(totalRevenue / totalBookings) : formatNZD(0)}
                </span>
              </SensitiveDataProtection>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('analytics.cancellationRate')}</span>
              <span className="text-sm font-semibold text-red-600">{cancellationRate.toFixed(1)}%</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;
